import React, { useState, useEffect } from 'react';
import { API_BASE, apiHeaders, apiGet, leseFallback, zeigeApiFehler } from '../../lib/api';
import { LdSheet } from '../../components/ui/LdSheet';
import { DiktatReview } from '../../components/diktat/DiktatReview';
import { DiktatAktion } from '../../lib/diktat-api';
import { toast } from '../../lib/toast';
import { SlotKlaerungModal } from '../slot-picking/SlotKlaerungModal';
import { MIcon } from '../../lib/micon';

interface KlaerungItem {
  id:           string;
  type:         string;
  titel:        string;
  beschreibung: string;
  erstellt_am:  string;
  aufnahme_id?: number;
  aktion_id?:   number;
  vorschlag_id?: number;
}

function relativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const min    = Math.floor(diffMs / 60_000);
  if (min < 60)   return `vor ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24)     return `vor ${h}h`;
  return `vor ${Math.floor(h / 24)}d`;
}

export function KlaerungInbox() {
  const [items,       setItems]       = useState<KlaerungItem[]>([]);
  const [laden,       setLaden]       = useState(true);
  const [reviewData,  setReviewData]  = useState<{ transcript: string | null; aktionen: DiktatAktion[] } | null>(null);
  const [reviewLaden, setReviewLaden] = useState(false);
  const [slotKlaerungId, setSlotKlaerungId] = useState<number | null>(null);

  function ladeItems() {
    fetch(`${API_BASE}/api/klaerung/items`, { headers: apiHeaders() })
      .then(leseFallback<{ items: KlaerungItem[] }>({ items: [] }, '/api/klaerung/items'))
      .then(d => setItems(d.items ?? []))
      .catch(() => {})
      .finally(() => setLaden(false));
  }

  useEffect(() => { ladeItems(); }, []);

  async function openReview(aufnahmeId: number) {
    setReviewLaden(true);
    try {
      const data = await apiGet<any>(`/api/klaerung/aufnahme/${aufnahmeId}/aktionen`);
      if (!data.aktionen?.length) {
        toast('Alle Aktionen dieser Aufnahme bereits erledigt.', 'info');
        ladeItems();
        return;
      }
      setReviewData({ transcript: data.aufnahme?.transcript ?? null, aktionen: data.aktionen });
    } catch (e) {
      zeigeApiFehler(e, 'Review konnte nicht geladen werden');
    } finally {
      setReviewLaden(false);
    }
  }

  function onReviewComplete(bestaetigt: number, verworfen: number) {
    toast(`${bestaetigt} bestätigt${verworfen > 0 ? `, ${verworfen} verworfen` : ''}`);
    setReviewData(null);
    ladeItems();
  }

  return (
    <div className="ld-page-enter" style={{ maxWidth: 700, margin: '0 auto', paddingBottom: 80 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 4px', color: 'var(--ld-text-primary)' }}>
          Klärung nötig
        </h1>
        <p style={{ fontSize: 13, color: 'var(--ld-text-secondary)', margin: 0 }}>
          Diktat-Aktionen mit niedriger Konfidenz — bitte überprüfen und bestätigen.
        </p>
      </div>

      {laden ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--ld-text-secondary)' }}>
          Lädt …
        </div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div style={{ marginBottom: 12, color: 'var(--ld-success-text)' }}><MIcon name="task_alt" size={44} /></div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ld-text-primary)' }}>Alles geklärt</div>
          <div style={{ fontSize: 13, color: 'var(--ld-text-secondary)', marginTop: 4 }}>
            Keine offenen Klärungs-Items.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(item => (
            <button
              key={item.id}
              onClick={() => item.type.startsWith('slot_') ? setSlotKlaerungId(item.vorschlag_id ?? null) : openReview(item.aufnahme_id!)}
              disabled={reviewLaden}
              style={{
                display: 'flex', flexDirection: 'column', gap: 5, textAlign: 'left',
                padding: '14px 16px', borderRadius: 'var(--ld-radius-xl)',
                border: '1.5px solid #FDE68A', background: '#FFFBEB',
                cursor: reviewLaden ? 'not-allowed' : 'pointer',
                opacity: reviewLaden ? 0.6 : 1,
                boxShadow: 'var(--ld-shadow-sm)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MIcon name="help" size={18} />
                <span style={{ fontSize: 14, fontWeight: 700, color: '#78350F', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.titel}
                </span>
                <span style={{ fontSize: 14, color: '#A16207', flexShrink: 0 }}>›</span>
              </div>
              <div style={{ fontSize: 12, color: '#A16207', paddingLeft: 24 }}>
                {item.beschreibung} · {relativeTime(item.erstellt_am)}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Review-Modal für ausgewählte Aufnahme */}
      <LdSheet
        open={reviewData !== null}
        onClose={() => setReviewData(null)}
        fullscreen
        title="Diktat-Aktionen überprüfen"
      >
        {reviewData && (
          <DiktatReview
            transcript={reviewData.transcript}
            aktionen={reviewData.aktionen}
            onComplete={onReviewComplete}
          />
        )}
      </LdSheet>

      <SlotKlaerungModal
        vorschlagId={slotKlaerungId}
        onClose={() => setSlotKlaerungId(null)}
        onResolved={ladeItems}
      />
    </div>
  );
}
